/**
 * Learn landing page — /learn
 * Hub linking to Blog and Glossary with latest posts
 * Fully redesigned with typewriter hero, 3D tilt cards, scroll-reveal, glossary chip strip
 */
import React, { useState, useEffect, useRef, useMemo } from "react";
import SiteLayout from "@/components/SiteLayout";
import { useSeoMeta } from "@/hooks/useSeoMeta";
import { useIsMobile } from "@/hooks/useIsMobile";
import { POSTS, CATEGORIES } from "@/lib/blogMeta.generated";
import { GLOSSARY_TERMS } from "@/data/glossary";
import { VIDEO_REVIEW_URL as STRATEGIST_URL } from "@/lib/cta";

// ─── Design tokens ────────────────────────────────────────────────────────────
const ff = "'Barlow', 'Barlow Fallback', sans-serif";
const cyan = "#57d7e2";
const tealText = "#0b8a96";

// ─── Data ─────────────────────────────────────────────────────────────────────
const CYCLING_WORDS = ["SEO", "Content", "AI Search", "Link Building", "B2B"];

const latestPosts = [...POSTS]
  .sort((a, b) => new Date(b.publishedAt).getTime() - new Date(a.publishedAt).getTime())
  .slice(0, 6);

function getCat(slug: string) {
  return CATEGORIES.find(c => c.slug === slug);
}

function formatDate(d: string) {
  return new Date(d).toLocaleDateString("en-US", { day: "numeric", month: "short", year: "numeric" });
}

const POPULAR_TERMS = [
  "ai-overview", "domain-rating", "keyword-difficulty", "tofu-mofu-bofu",
  "zero-click-search", "backlink", "search-intent", "core-web-vitals",
  "e-e-a-t", "serp-features",
];

// ─── Hooks ────────────────────────────────────────────────────────────────────

function useReveal(delay = 0) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setVisible(true), delay);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [delay]);

  const style: React.CSSProperties = {
    opacity: visible ? 1 : 0,
    transform: visible ? "translateY(0)" : "translateY(28px)",
    transition: "opacity 0.6s ease, transform 0.6s ease",
  };

  return { ref, style };
}

function useTypewriter(
  words: string[],
  typingSpeed = 80,
  deletingSpeed = 45,
  pauseMs = 2000
) {
  const [wordIdx, setWordIdx] = useState(0);
  const [charIdx, setCharIdx] = useState(CYCLING_WORDS[0].length);
  const [phase, setPhase] = useState<"typing" | "pause" | "deleting">("pause");
  const [displayed, setDisplayed] = useState(CYCLING_WORDS[0]);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>;

    if (phase === "pause") {
      timer = setTimeout(() => setPhase("deleting"), pauseMs);
    } else if (phase === "deleting") {
      if (charIdx > 0) {
        timer = setTimeout(() => {
          setCharIdx(c => c - 1);
          setDisplayed(words[wordIdx].slice(0, charIdx - 1));
        }, deletingSpeed);
      } else {
        const next = (wordIdx + 1) % words.length;
        setWordIdx(next);
        setCharIdx(0);
        setDisplayed("");
        setPhase("typing");
      }
    } else {
      // typing
      const w = words[wordIdx];
      if (charIdx < w.length) {
        timer = setTimeout(() => {
          setCharIdx(c => c + 1);
          setDisplayed(w.slice(0, charIdx + 1));
        }, typingSpeed);
      } else {
        setPhase("pause");
      }
    }

    return () => clearTimeout(timer);
  }, [phase, charIdx, wordIdx, words, typingSpeed, deletingSpeed, pauseMs]);

  return displayed;
}

function use3DTilt() {
  const onMouseMove = (e: React.MouseEvent<HTMLElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    (e.currentTarget as HTMLElement).style.transform = `perspective(600px) rotateX(${-y * 7}deg) rotateY(${x * 7}deg) translateY(-3px)`;
  };
  const onMouseLeave = (e: React.MouseEvent<HTMLElement>) => {
    (e.currentTarget as HTMLElement).style.transform =
      "perspective(600px) rotateX(0deg) rotateY(0deg) translateY(0)";
  };
  return { onMouseMove, onMouseLeave };
}

// ─── TermChip ─────────────────────────────────────────────────────────────────

function TermChip({ term }: { term: { slug: string; term: string; shortDef: string; category: string } }) {
  const [hov, setHov] = useState(false);

  return (
    <div style={{ position: "relative", display: "inline-block" }}>
      <a
        href={`/glossary/${term.slug}`}
        onMouseEnter={() => setHov(true)}
        onMouseLeave={() => setHov(false)}
        style={{
          display: "inline-flex",
          alignItems: "center",
          padding: "8px 14px",
          borderRadius: "8px",
          border: `1px solid ${hov ? "rgba(87,215,226,0.3)" : "rgba(255,255,255,0.1)"}`,
          background: hov ? "rgba(87,215,226,0.08)" : "transparent",
          color: hov ? cyan : "rgba(255,255,255,0.55)",
          fontSize: "14px",
          fontWeight: 600,
          fontFamily: ff,
          textDecoration: "none",
          transition: "all 0.15s",
          cursor: "pointer",
          whiteSpace: "nowrap",
        }}
      >
        {term.term}
      </a>
      {hov && (
        <div
          style={{
            position: "absolute",
            zIndex: 50,
            bottom: "calc(100% + 10px)",
            left: "50%",
            transform: "translateX(-50%)",
            width: "240px",
            background: "#1a1a1a",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: "10px",
            padding: "14px 16px",
            boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
            pointerEvents: "none",
            animation: "popoverIn 0.15s ease forwards",
          }}
        >
          <div style={{ color: "white", fontSize: "13px", fontWeight: 700, fontFamily: ff, marginBottom: "6px" }}>
            {term.term}
          </div>
          <div style={{ color: "rgba(255,255,255,0.5)", fontSize: "12px", fontFamily: ff, lineHeight: 1.6 }}>
            {term.shortDef}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── BlogCard ─────────────────────────────────────────────────────────────────

function BlogCard({ post, isMobile }: { post: typeof POSTS[0]; isMobile: boolean }) {
  const [hov, setHov] = useState(false);
  const tilt = use3DTilt();
  const cat = post.categories[0] ? getCat(post.categories[0]) : null;

  return (
    <a
      href={`/blog/${post.slug}`}
      onMouseEnter={() => setHov(true)}
      onMouseMove={tilt.onMouseMove}
      onMouseLeave={(e) => { setHov(false); tilt.onMouseLeave(e); }}
      style={{
        display: "block",
        textDecoration: "none",
        borderRadius: "16px",
        overflow: "hidden",
        border: `1px solid ${hov ? "rgba(87,215,226,0.25)" : "rgba(0,0,0,0.08)"}`,
        background: "white",
        boxShadow: hov ? "0 8px 32px rgba(0,0,0,0.1)" : "0 1px 4px rgba(0,0,0,0.05)",
        transition: "border-color 0.2s, box-shadow 0.2s",
      }}
    >
      {post.coverImage && (
        <div style={{ aspectRatio: "16/9", overflow: "hidden" }}>
          <img
            loading="lazy"
            src={post.coverImage}
            alt={post.title}
            style={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              display: "block",
              transition: "transform 0.3s",
              transform: hov ? "scale(1.04)" : "scale(1)",
            }}
          />
        </div>
      )}
      <div style={{ padding: isMobile ? "18px 18px 22px" : "22px 24px 26px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
          {cat && (
            <span style={{
              color: cat.color ?? cyan,
              fontSize: "11px",
              fontWeight: 700,
              fontFamily: ff,
              textTransform: "uppercase",
              letterSpacing: "0.05em",
            }}>
              {cat.name}
            </span>
          )}
          <span style={{ color: "rgba(0,0,0,0.35)", fontSize: "12px", fontFamily: ff }}>
            {formatDate(post.publishedAt)}
          </span>
        </div>
        <h3 style={{
          color: "#0a0a0a",
          fontSize: "16px",
          fontWeight: 700,
          fontFamily: ff,
          lineHeight: 1.35,
          margin: 0,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}>
          {post.title}
        </h3>
      </div>
    </a>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

const CSS_KEYFRAMES = `
@keyframes blobDrift { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(38px,-28px) scale(1.07)} 66%{transform:translate(-22px,16px) scale(0.94)} }
@keyframes blobDrift2 { 0%,100%{transform:translate(0,0) scale(1)} 33%{transform:translate(-32px,20px) scale(1.05)} 66%{transform:translate(26px,-14px) scale(0.96)} }
@keyframes blinkCursor { 0%,100%{opacity:1} 50%{opacity:0} }
@keyframes fadeUp { from{opacity:0;transform:translateY(24px)} to{opacity:1;transform:translateY(0)} }
@keyframes popoverIn { from{opacity:0;transform:translateY(6px) scale(0.97)} to{opacity:1;transform:translateY(0) scale(1)} }
@keyframes tabSlide { 0%{opacity:0} 100%{opacity:1} }
`;

export default function LearnIndex() {
  const isMobile = useIsMobile();
  const displayedWord = useTypewriter(CYCLING_WORDS);

  const [activeCategory, setActiveCategory] = useState("all");

  // Reveal hooks for hub cards
  const revealBlog = useReveal(0);
  const revealGlossary = useReveal(120);

  // Reveal hooks for blog post cards (staggered)
  const revealPost0 = useReveal(0);
  const revealPost1 = useReveal(60);
  const revealPost2 = useReveal(120);
  const revealPost3 = useReveal(180);
  const revealPost4 = useReveal(240);
  const revealPost5 = useReveal(300);
  const postReveals = [revealPost0, revealPost1, revealPost2, revealPost3, revealPost4, revealPost5];

  useSeoMeta({
    title: "Learn SEO & B2B Growth — Blog, Guides & Glossary — Indexed",
    description: "Learn SEO and B2B growth with practitioner-written guides, tactics, and a full glossary. Build an organic program that generates real revenue.",
    canonical: "/learn",
    ogType: "website",
    jsonLd: [{
      "@type": "CollectionPage",
      "name": "Indexed Learning Hub — SEO & B2B Growth",
      "description": "SEO articles, strategy guides, and glossary terms from the Indexed team.",
      "url": "https://www.joinindexed.com/learn",
      "publisher": { "@type": "Organization", "name": "Indexed", "url": "https://www.joinindexed.com" }
    }],
  });

  const popularTerms = useMemo(
    () =>
      POPULAR_TERMS.map(slug => GLOSSARY_TERMS.find(t => t.slug === slug)).filter(
        (t): t is typeof GLOSSARY_TERMS[0] => Boolean(t)
      ),
    []
  );

  const tabCategories = ["all", ...[...new Set(latestPosts.flatMap(p => p.categories))].slice(0, 5)];

  const filteredPosts =
    activeCategory === "all"
      ? latestPosts
      : latestPosts.filter(p => p.categories.includes(activeCategory));

  // Blog card tilt — used inline on hub cards
  const blogTilt = use3DTilt();
  const glossaryTilt = use3DTilt();

  return (
    <SiteLayout>
      <style>{CSS_KEYFRAMES}</style>

      {/* ─── Section 1: Hero ─────────────────────────────────────────────────── */}
      <section
        style={{
          position: "relative",
          overflow: "hidden",
          background: "#0a0a0a",
          padding: isMobile ? "140px 20px 56px" : "180px 72px 72px",
        }}
      >
        {/* Dot grid */}
        <div
          style={{
            position: "absolute",
            inset: 0,
            zIndex: 0,
            pointerEvents: "none",
            backgroundImage: "radial-gradient(rgba(255,255,255,0.055) 1px, transparent 1px)",
            backgroundSize: "24px 24px",
          }}
        />

        {/* Blob 1 */}
        <div
          style={{
            position: "absolute",
            width: "550px",
            height: "550px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(87,215,226,0.1) 0%, transparent 70%)",
            top: "-180px",
            right: "-120px",
            zIndex: 0,
            pointerEvents: "none",
            animation: "blobDrift 15s ease-in-out infinite",
          }}
        />

        {/* Blob 2 */}
        <div
          style={{
            position: "absolute",
            width: "380px",
            height: "380px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(87,215,226,0.07) 0%, transparent 70%)",
            bottom: "-80px",
            left: "-100px",
            zIndex: 0,
            pointerEvents: "none",
            animation: "blobDrift2 19s ease-in-out infinite",
          }}
        />

        {/* Content */}
        <div style={{ maxWidth: "900px", margin: "0 auto", position: "relative", zIndex: 1 }}>
          {/* Label */}
          <div style={{
            color: cyan,
            fontSize: "11px",
            fontFamily: ff,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            marginBottom: "20px",
          }}>
            Learn · SEO &amp; B2B Growth
          </div>

          {/* H1 */}
          <h1
            style={{
              color: "white",
              fontSize: isMobile ? "34px" : "clamp(38px,4.5vw,54px)",
              fontWeight: 800,
              fontFamily: ff,
              lineHeight: 1.1,
              letterSpacing: "-0.02em",
              margin: 0,
            }}
          >
            {"Learn how modern "}
            <span style={{ position: "relative", display: "inline-block" }}>
              {/* Ghost — holds space for the longest word so the line never reflows */}
              <span style={{ visibility: "hidden", pointerEvents: "none", whiteSpace: "nowrap", color: cyan }}>
                Link Building
              </span>
              {/* Actual typed text overlaid absolutely — zero layout impact */}
              <span style={{ position: "absolute", left: 0, top: 0, whiteSpace: "nowrap", color: cyan }}>
                {displayedWord}
                <span
                  style={{
                    display: "inline-block",
                    width: "2px",
                    height: "0.85em",
                    background: cyan,
                    marginLeft: "2px",
                    verticalAlign: "middle",
                    animation: "blinkCursor 0.85s step-end infinite",
                  }}
                />
              </span>
            </span>
            {isMobile ? <span style={{ display: "block" }}>actually works.</span> : " actually works."}
          </h1>

          {/* Subtitle */}
          <p
            style={{
              color: "rgba(255,255,255,0.5)",
              fontSize: "17px",
              fontFamily: ff,
              lineHeight: 1.65,
              maxWidth: "560px",
              marginTop: "18px",
              marginBottom: 0,
            }}
          >
            Strategy, tactics, and terminology from the Indexed team. Everything you need to build an SEO program that generates revenue.
          </p>

          {/* Stats row */}
          <div
            style={{
              display: "flex",
              gap: "36px",
              flexWrap: "wrap",
              marginTop: "36px",
            }}
          >
            {[
              { num: "47", label: "articles published" },
              { num: "55", label: "glossary terms" },
              { num: "Free", label: "to read" },
            ].map(stat => (
              <div key={stat.label}>
                <span style={{ color: "white", fontWeight: 700, fontSize: "13px", fontFamily: ff }}>
                  {stat.num}
                </span>{" "}
                <span style={{ color: "rgba(255,255,255,0.3)", fontSize: "13px", fontFamily: ff }}>
                  {stat.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 2: Hub cards ─────────────────────────────────────────────── */}
      <section
        style={{
          background: "#0a0a0a",
          padding: isMobile ? "0 20px 64px" : "0 72px 64px",
        }}
      >
        <div
          style={{
            maxWidth: "900px",
            margin: "0 auto",
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
            gap: "16px",
          }}
        >
          {/* Blog hub card */}
          <div ref={revealBlog.ref} style={revealBlog.style}>
            <a
              href="/blog"
              onMouseMove={blogTilt.onMouseMove}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = "rgba(87,215,226,0.25)";
                e.currentTarget.style.background = "rgba(87,215,226,0.03)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
                e.currentTarget.style.background = "#111";
                blogTilt.onMouseLeave(e);
              }}
              style={{
                display: "flex",
                flexDirection: "column",
                textDecoration: "none",
                background: "#111",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: "20px",
                padding: "36px 32px",
                minHeight: "220px",
                transition: "border-color 0.2s, background 0.2s",
              }}
            >
              {/* Icon box */}
              <div style={{
                width: "56px",
                height: "56px",
                borderRadius: "14px",
                background: "rgba(87,215,226,0.06)",
                border: "1px solid rgba(87,215,226,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <rect x="4" y="3" width="20" height="22" rx="2.5" stroke={cyan} strokeWidth="1.6" opacity="0.85" />
                  <path d="M9 9h10M9 13h10M9 17h6" stroke={cyan} strokeWidth="1.4" strokeLinecap="round" opacity="0.5" />
                </svg>
              </div>

              {/* Badge */}
              <div style={{ marginTop: "16px", marginBottom: "2px" }}>
                <span style={{
                  background: "rgba(87,215,226,0.08)",
                  border: "1px solid rgba(87,215,226,0.15)",
                  color: cyan,
                  fontSize: "10px",
                  fontFamily: ff,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  borderRadius: "4px",
                  padding: "3px 8px",
                }}>
                  47 articles
                </span>
              </div>

              <h2 style={{ color: "white", fontSize: "24px", fontWeight: 700, fontFamily: ff, marginTop: "16px", marginBottom: "10px" }}>
                Blog
              </h2>
              <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "15px", fontFamily: ff, lineHeight: 1.65, flex: 1, margin: 0 }}>
                In-depth articles on SEO strategy, AI search, content systems, and B2B organic growth.
              </p>
              <span style={{ color: cyan, fontSize: "14px", fontWeight: 600, fontFamily: ff, marginTop: "20px", display: "block" }}>
                Browse all articles →
              </span>
            </a>
          </div>

          {/* Glossary hub card */}
          <div ref={revealGlossary.ref} style={revealGlossary.style}>
            <a
              href="/glossary"
              onMouseMove={glossaryTilt.onMouseMove}
              onMouseEnter={e => {
                e.currentTarget.style.borderColor = "rgba(87,215,226,0.25)";
                e.currentTarget.style.background = "rgba(87,215,226,0.03)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)";
                e.currentTarget.style.background = "#111";
                glossaryTilt.onMouseLeave(e);
              }}
              style={{
                display: "flex",
                flexDirection: "column",
                textDecoration: "none",
                background: "#111",
                border: "1px solid rgba(255,255,255,0.07)",
                borderRadius: "20px",
                padding: "36px 32px",
                minHeight: "220px",
                transition: "border-color 0.2s, background 0.2s",
              }}
            >
              {/* Icon box */}
              <div style={{
                width: "56px",
                height: "56px",
                borderRadius: "14px",
                background: "rgba(87,215,226,0.06)",
                border: "1px solid rgba(87,215,226,0.12)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}>
                <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
                  <path d="M6 5h16a2 2 0 012 2v14a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2z" stroke={cyan} strokeWidth="1.6" opacity="0.85" />
                  <path d="M14 5v18" stroke={cyan} strokeWidth="1.4" opacity="0.3" />
                  <path d="M9 10h2M9 14h3M17 10h2M17 14h3" stroke={cyan} strokeWidth="1.4" strokeLinecap="round" opacity="0.5" />
                </svg>
              </div>

              {/* Badge */}
              <div style={{ marginTop: "16px", marginBottom: "2px" }}>
                <span style={{
                  background: "rgba(87,215,226,0.08)",
                  border: "1px solid rgba(87,215,226,0.15)",
                  color: cyan,
                  fontSize: "10px",
                  fontFamily: ff,
                  fontWeight: 700,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  borderRadius: "4px",
                  padding: "3px 8px",
                }}>
                  55 terms
                </span>
              </div>

              <h2 style={{ color: "white", fontSize: "24px", fontWeight: 700, fontFamily: ff, marginTop: "16px", marginBottom: "10px" }}>
                Glossary
              </h2>
              <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "15px", fontFamily: ff, lineHeight: 1.65, flex: 1, margin: 0 }}>
                SEO and marketing terms explained clearly. A reference for practitioners and leadership alike.
              </p>
              <span style={{ color: cyan, fontSize: "14px", fontWeight: 600, fontFamily: ff, marginTop: "20px", display: "block" }}>
                Browse glossary →
              </span>
            </a>
          </div>
        </div>
      </section>

      {/* ─── Section 3: Popular glossary terms ───────────────────────────────── */}
      <section
        style={{
          background: "#0c0c0c",
          borderTop: "1px solid rgba(255,255,255,0.05)",
          padding: isMobile ? "40px 20px 48px" : "56px 72px",
        }}
      >
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <div style={{
            color: "rgba(255,255,255,0.3)",
            fontSize: "11px",
            fontFamily: ff,
            fontWeight: 600,
            textTransform: "uppercase",
            letterSpacing: "0.08em",
            marginBottom: "20px",
          }}>
            Popular glossary terms
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {popularTerms.map(term => (
              <TermChip key={term.slug} term={term} />
            ))}
          </div>
        </div>
      </section>

      {/* ─── Section 4: Blog posts (white) ───────────────────────────────────── */}
      <section
        style={{
          background: "#ffffff",
          borderTop: "1px solid rgba(0,0,0,0.08)",
          padding: isMobile ? "48px 20px 64px" : "72px 72px 80px",
        }}
      >
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          {/* Heading */}
          <h2 style={{
            color: "#0a0a0a",
            fontSize: isMobile ? "24px" : "30px",
            fontWeight: 800,
            fontFamily: ff,
            marginBottom: "8px",
            lineHeight: 1.15,
          }}>
            Latest from the blog
          </h2>
          <p style={{
            color: "rgba(0,0,0,0.4)",
            fontSize: "15px",
            fontFamily: ff,
            lineHeight: 1.6,
            marginBottom: "32px",
          }}>
            In-depth articles on B2B SEO, AI search, and organic growth strategy.
          </p>

          {/* Category filter pills */}
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap", marginBottom: "36px" }}>
            {tabCategories.map(slug => {
              const isActive = activeCategory === slug;
              const label = slug === "all" ? "All" : (getCat(slug)?.name ?? slug);
              return (
                <button
                  key={slug}
                  onClick={() => setActiveCategory(slug)}
                  style={{
                    padding: "7px 16px",
                    borderRadius: "20px",
                    fontSize: "13px",
                    fontWeight: 600,
                    fontFamily: ff,
                    border: "none",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    background: isActive ? "#0a0a0a" : "rgba(0,0,0,0.06)",
                    color: isActive ? "white" : "rgba(0,0,0,0.5)",
                  }}
                >
                  {label}
                </button>
              );
            })}
          </div>

          {/* Post grid */}
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr 1fr",
            gap: "20px",
          }}>
            {filteredPosts.map((post, i) => {
              const reveal = postReveals[i] ?? postReveals[0];
              return (
                <div key={post.slug} ref={reveal.ref} style={reveal.style}>
                  <BlogCard post={post} isMobile={isMobile} />
                </div>
              );
            })}
          </div>

          {/* View all link */}
          <a
            href="/blog"
            style={{
              display: "block",
              textAlign: "center",
              marginTop: "32px",
              color: tealText,
              fontWeight: 600,
              fontFamily: ff,
              fontSize: "14px",
              textDecoration: "none",
            }}
            onMouseEnter={e => { e.currentTarget.style.textDecoration = "underline"; }}
            onMouseLeave={e => { e.currentTarget.style.textDecoration = "none"; }}
          >
            View all articles →
          </a>
        </div>
      </section>

      {/* ─── Section 5: CTA ──────────────────────────────────────────────────── */}
      <section
        style={{
          background: "#0c0c0c",
          padding: isMobile ? "60px 20px" : "80px 72px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "640px", margin: "0 auto" }}>
          <h2
            style={{
              color: "white",
              fontSize: isMobile ? "26px" : "34px",
              fontWeight: 700,
              fontFamily: ff,
              lineHeight: 1.15,
              marginBottom: "14px",
            }}
          >
            Want this applied to your business?
          </h2>
          <p
            style={{
              color: "rgba(255,255,255,0.45)",
              fontSize: "16px",
              fontFamily: ff,
              lineHeight: 1.6,
              marginBottom: "28px",
              maxWidth: "540px",
              margin: "0 auto 28px",
            }}
          >
            Talk to a strategist about how Indexed can build an SEO system that generates measurable revenue for your company.
          </p>
          <a
            href={STRATEGIST_URL}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "16px 32px",
              background: "linear-gradient(135deg, #85e2e9 0%, #20b0bc 100%)",
              borderRadius: "88px",
              border: "1px solid #57d7e2",
              boxShadow: "0px 4px 20px rgba(87,215,226,0.25)",
              textDecoration: "none",
              color: "#0a0c0c",
              fontSize: "15px",
              fontWeight: 700,
              fontFamily: ff,
              transition: "opacity 0.2s, transform 0.2s",
            }}
            onMouseEnter={e => {
              e.currentTarget.style.opacity = "0.88";
              e.currentTarget.style.transform = "translateY(-1px)";
            }}
            onMouseLeave={e => {
              e.currentTarget.style.opacity = "1";
              e.currentTarget.style.transform = "translateY(0)";
            }}
          >
            Get my free video review →
          </a>
        </div>
      </section>
    </SiteLayout>
  );
}
